import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isSafeInsightHref } from '../lib/ai-cards';

/** A name the server said may become a link. The href is checked again here. */
export type InsightTextLink = { label: string; href: string };

/**
 * Minimal, safe Markdown renderer for the assistant's answers. The TruInsight model emits a
 * constrained subset — paragraphs, **bold**, `inline code`, and `-`/`1.` lists — so a tiny
 * purpose-built renderer beats pulling in a full Markdown dependency (security.mdc §5: no new
 * runtime deps without review). Everything renders to React text nodes (no
 * `dangerouslySetInnerHTML`), so model output is escaped by default and cannot inject markup
 * (security.mdc §1).
 *
 * It is deliberately tolerant of partial input (an unclosed `**` mid-stream renders literally)
 * because it is fed token-by-token while a response streams in.
 */

const BOLD = '**';
const CODE = '`';

function readyLinks(links: InsightTextLink[] | undefined): InsightTextLink[] {
  if (!links) return [];
  return links
    .filter((link) => link.label.trim().length >= 2 && isSafeInsightHref(link.href))
    .slice()
    .sort((a, b) => b.label.length - a.label.length);
}

function isWordChar(ch: string | undefined): boolean {
  return ch != null && /[A-Za-z0-9]/.test(ch);
}

/**
 * Turn exact name matches into router links. Case must match too: folders "test" and "Ryan Test"
 * can sit side by side, and "Test" in the second must not open the first. A match inside a longer
 * word is left as text.
 */
function linkify(text: string, links: InsightTextLink[], keyPrefix: string): ReactNode[] {
  if (links.length === 0 || text.length === 0) return [text];
  const nodes: ReactNode[] = [];
  let buffer = '';
  let i = 0;
  let key = 0;
  const flush = () => {
    if (!buffer) return;
    nodes.push(<Fragment key={`${keyPrefix}-t${key++}`}>{buffer}</Fragment>);
    buffer = '';
  };
  while (i < text.length) {
    const hit = links.find((link) => {
      const size = link.label.length;
      if (text.slice(i, i + size) !== link.label) return false;
      return !isWordChar(text[i - 1]) && !isWordChar(text[i + size]);
    });
    if (!hit) {
      buffer += text[i];
      i += 1;
      continue;
    }
    flush();
    nodes.push(
      <Link
        key={`${keyPrefix}-a${key++}`}
        to={hit.href}
        className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {text.slice(i, i + hit.label.length)}
      </Link>,
    );
    i += hit.label.length;
  }
  flush();
  return nodes.length > 0 ? nodes : [text];
}

/** Render inline `**bold**` and `` `code` `` spans within a single line; everything else is text. */
function renderInline(text: string, keyPrefix: string, links: InsightTextLink[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  let buffer = '';
  let i = 0;
  let key = 0;

  const flush = () => {
    if (buffer) {
      nodes.push(...linkify(buffer, links, `${keyPrefix}-l${key++}`));
      buffer = '';
    }
  };

  while (i < text.length) {
    if (text.startsWith(BOLD, i)) {
      const end = text.indexOf(BOLD, i + BOLD.length);
      if (end !== -1) {
        flush();
        nodes.push(
          <strong key={`${keyPrefix}-b${key++}`}>
            {linkify(text.slice(i + BOLD.length, end), links, `${keyPrefix}-b${key}`)}
          </strong>,
        );
        i = end + BOLD.length;
        continue;
      }
    } else if (text[i] === CODE) {
      const end = text.indexOf(CODE, i + 1);
      if (end !== -1) {
        flush();
        nodes.push(
          <code
            key={`${keyPrefix}-c${key++}`}
            className="rounded bg-background px-1 py-0.5 font-mono text-[0.85em]"
          >
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    buffer += text[i];
    i += 1;
  }
  flush();
  return nodes;
}

const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+\.\s+(.*)$/;
/** ATX-style heading (`#`..`######`). We don't render heading chrome — strip the hashes and bold it. */
const HEADING_RE = /^\s*#{1,6}\s+(.*)$/;

/** Render one block (paragraph or list) separated from the next by a blank line. */
function Block({ text, blockKey, links }: { text: string; blockKey: string; links: InsightTextLink[] }) {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  if (lines.length === 0) return null;

  if (lines.every((l) => BULLET_RE.test(l))) {
    return (
      <ul className="ml-4 list-disc space-y-0.5">
        {lines.map((l, i) => (
          <li key={`${blockKey}-li${i}`}>{renderInline(l.replace(BULLET_RE, '$1'), `${blockKey}-li${i}`, links)}</li>
        ))}
      </ul>
    );
  }

  if (lines.every((l) => ORDERED_RE.test(l))) {
    return (
      <ol className="ml-4 list-decimal space-y-0.5">
        {lines.map((l, i) => (
          <li key={`${blockKey}-oi${i}`}>{renderInline(l.replace(ORDERED_RE, '$1'), `${blockKey}-oi${i}`, links)}</li>
        ))}
      </ol>
    );
  }

  // Paragraph fallback. A `#`-heading line is not rendered as rich heading chrome — we strip the
  // hashes and emphasize it inline so the raw `#` never leaks into the answer (the model is also
  // told to avoid headings; this is the defensive backstop for streamed/edge output).
  return (
    <div className="space-y-1">
      {lines.map((l, i) => {
        const heading = l.match(HEADING_RE);
        if (heading) {
          return (
            <p key={`${blockKey}-h${i}`} className="font-semibold text-foreground">
              {renderInline(heading[1], `${blockKey}-h${i}`, links)}
            </p>
          );
        }
        return (
          <p key={`${blockKey}-p${i}`} className="whitespace-pre-wrap">
            {renderInline(l, `${blockKey}-p${i}`, links)}
          </p>
        );
      })}
    </div>
  );
}

export function MarkdownLite({ content, links }: { content: string; links?: InsightTextLink[] }) {
  const ready = readyLinks(links);
  const blocks = content.split(/\n{2,}/);
  return (
    <div className="space-y-2">
      {blocks.map((block, i) => (
        <Block key={`blk${i}`} text={block} blockKey={`blk${i}`} links={ready} />
      ))}
    </div>
  );
}
